<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Checkbox Component
      p.text--secondary Checkboxes allow users to select one or more options from a set.

    // Checkbox Variations
    .component-section.mb-8#basic-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-variations')") Basic Variations
      .variations-grid
        .variation-item.mb-6(v-for="variation in checkboxVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          // Grouped checkboxes (side by side or vertical)
          template(v-if="variation.isGroup")
            .variation-preview.mb-4.pa-4.border.rounded
              // Special handling for layout variations
              template(v-if="variation.id === 'layout-variations'")
                div(v-for="checkbox in variation.checkboxes" :key="checkbox.value" class="mb-4")
                  h5.mb-2 {{ checkbox.label }}
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify($data[checkbox.model]) }}
                  // Vertical default
                  yz-checkbox-group(v-if="checkbox.layoutType === 'vertical-default'" v-model="$data[checkbox.model]")
                    yz-checkbox(value="layout-a" label="Layout A")
                    yz-checkbox(value="layout-b" label="Layout B")
                    yz-checkbox(value="layout-c" label="Layout C")
                  // Vertical with 32px spacing
                  yz-checkbox-group(v-else-if="checkbox.layoutType === 'vertical-32'" v-model="$data[checkbox.model]" spacing="32px")
                    yz-checkbox(value="layout-a" label="Layout A")
                    yz-checkbox(value="layout-b" label="Layout B")
                    yz-checkbox(value="layout-c" label="Layout C")
                  // Horizontal default
                  yz-checkbox-group(v-else-if="checkbox.layoutType === 'horizontal-default'" v-model="$data[checkbox.model]" direction="horizontal")
                    yz-checkbox(value="layout-a" label="Layout A")
                    yz-checkbox(value="layout-b" label="Layout B")
                    yz-checkbox(value="layout-c" label="Layout C")
                  // Horizontal with 32px spacing
                  yz-checkbox-group(v-else-if="checkbox.layoutType === 'horizontal-32'" v-model="$data[checkbox.model]" direction="horizontal" spacing="32px")
                    yz-checkbox(value="layout-a" label="Layout A")
                    yz-checkbox(value="layout-b" label="Layout B")
                    yz-checkbox(value="layout-c" label="Layout C")
              // Special handling for label slot examples
              template(v-else-if="variation.id === 'label-slot'")
                .mb-3
                  strong Selected: {{ JSON.stringify($data[variation.checkboxes[0].model]) }}
                yz-checkbox-group(v-model="$data[variation.checkboxes[0].model]")
                  template(v-for="checkbox in variation.checkboxes")
                    // Regular label prop
                    yz-checkbox(v-if="!checkbox.isSlot" :key="checkbox.value" :value="checkbox.value" :label="checkbox.label" v-bind="checkbox.props")
                    // Slot with HTML content
                    yz-checkbox(v-else-if="checkbox.isSlot && !checkbox.isRich" :key="checkbox.value" :value="checkbox.value" v-bind="checkbox.props")
                      template(#label)
                        span(v-html="'Custom slot with <strong class=\"text-danger-500\">HTML content</strong>'")
                    // Rich content slot
                    yz-checkbox(v-else-if="checkbox.isRich" :key="checkbox.value" :value="checkbox.value" v-bind="checkbox.props")
                      template(#label)
                        .d-flex.align-center
                          v-icon.mr-2(small color="info500") mdi-star
                          span Premium Option
              // Special handling for validation rules example
              template(v-else-if="variation.id === 'validation-rules'")
                .mb-4
                  h5.mb-2 Required validation
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue) }}
                  yz-checkbox-group(ref="rulesCheckboxGroup" v-model="rulesCheckboxValue" required, requiredErrorMessage="Please select at least one option")
                    yz-checkbox(value="option1" label="Option 1")
                    yz-checkbox(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetRulesCheckboxGroup")
                      yz-btn(label="Validate" @click="validateRulesCheckboxGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="rulesValidationResult" :class="{ 'success--text': rulesValidationResult === 'Valid', 'error--text': rulesValidationResult === 'Invalid' }") {{ rulesValidationResult }}
                
                .mb-4
                  h5.mb-2 Combined required + custom validation rules
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue2) }}
                  yz-checkbox-group(ref="customRulesCheckboxGroup" v-model="rulesCheckboxValue2" required, requiredErrorMessage="Please select at least one option" :rules="[v => v.includes('option1') || 'Option 1 must be selected']")
                    yz-checkbox(value="option1" label="Option 1")
                    yz-checkbox(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetCustomRulesCheckboxGroup")
                      yz-btn(label="Validate" @click="validateCustomRulesCheckboxGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="customRulesValidationResult" :class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}
                
                .mb-4
                  h5.mb-2 Required validation using only rules (no required prop)
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(rulesCheckboxValue3) }}
                  yz-checkbox-group(ref="rulesOnlyCheckboxGroup" v-model="rulesCheckboxValue3" :rules="[v => (v && v.length > 0) || 'At least one option is required']")
                    yz-checkbox(value="option1" label="Option 1")
                    yz-checkbox(value="option2" label="Option 2")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetRulesOnlyCheckboxGroup")
                      yz-btn(label="Validate" @click="validateRulesOnlyCheckboxGroup" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="rulesOnlyValidationResult" :class="{ 'success--text': rulesOnlyValidationResult === 'Valid', 'error--text': rulesOnlyValidationResult === 'Invalid' }") {{ rulesOnlyValidationResult }}
              
              // Optional validation examples
              template(v-else-if="variation.id === 'optional-validation'")
                .mb-4
                  h5.mb-2 Optional feature preferences
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalFeaturesValue) }}
                  v-form(ref="optionalFeaturesForm")
                    yz-checkbox-group(v-model="optionalFeaturesValue" :rules="optionalFeaturesRules")
                      yz-checkbox(value="notifications" label="Email notifications")
                      yz-checkbox(value="analytics" label="Usage analytics")
                      yz-checkbox(value="beta" label="Beta features access")
                      yz-checkbox(value="marketing" label="Marketing communications")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetOptionalFeaturesForm")
                      yz-btn(label="Validate" @click="validateOptionalFeaturesForm" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="optionalFeaturesValidationResult" :class="{ 'success--text': optionalFeaturesValidationResult === 'Valid', 'error--text': optionalFeaturesValidationResult === 'Invalid' }") {{ optionalFeaturesValidationResult }}
                
                .mb-4
                  h5.mb-2 Optional consent preferences
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalConsentValue) }}
                  v-form(ref="optionalConsentForm")
                    yz-checkbox-group(v-model="optionalConsentValue" :rules="optionalConsentRules")
                      yz-checkbox(value="terms" label="I agree to the terms of service")
                      yz-checkbox(value="privacy" label="I accept the privacy policy")
                      yz-checkbox(value="cookies" label="I allow cookies for analytics")
                      yz-checkbox(value="newsletter" label="Subscribe to newsletter")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetOptionalConsentForm")
                      yz-btn(label="Validate" @click="validateOptionalConsentForm" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="optionalConsentValidationResult" :class="{ 'success--text': optionalConsentValidationResult === 'Valid', 'error--text': optionalConsentValidationResult === 'Invalid' }") {{ optionalConsentValidationResult }}
                
                .mb-4
                  h5.mb-2 Optional addon services
                  .mb-2
                    small.text--secondary Selected: {{ JSON.stringify(optionalAddonsValue) }}
                  v-form(ref="optionalAddonsForm")
                    yz-checkbox-group(v-model="optionalAddonsValue" :rules="optionalAddonsRules")
                      yz-checkbox(value="backup" label="Cloud backup service")
                      yz-checkbox(value="support" label="Priority customer support")
                      yz-checkbox(value="training" label="Training and onboarding")
                      yz-checkbox(value="integration" label="Third-party integrations")
                  .mt-4
                    .d-flex.gap-2
                      yz-btn(label="Reset" size="sm" @click="resetOptionalAddonsForm")
                      yz-btn(label="Validate" @click="validateOptionalAddonsForm" size="sm" outlined)
                    span.yz-regular-body-xs.ml-2(v-if="optionalAddonsValidationResult" :class="{ 'success--text': optionalAddonsValidationResult === 'Valid', 'error--text': optionalAddonsValidationResult === 'Invalid' }") {{ optionalAddonsValidationResult }}
              
              // Special handling for indeterminate state examples
              template(v-else-if="variation.id === 'indeterminate-state'")
                div(v-for="checkbox in variation.checkboxes" :key="checkbox.value" class="mb-6")
                  h5.mb-2 {{ checkbox.label }}
                  .mb-2
                    small.text--secondary Current state and values shown below

                  // Basic indeterminate example
                  template(v-if="checkbox.value === 'basic-indeterminate'")
                    .mb-2
                      small.text--secondary Indeterminate: {{ basicIndeterminate ? 'true' : 'false' }}
                    yz-checkbox(v-model="basicIndeterminate" :indeterminate="true" label="Basic Indeterminate (always indeterminate)")


                  // Indeterminate + Disabled example
                  template(v-else-if="checkbox.value === 'indeterminate-disabled'")
                    .mb-2
                      small.text--secondary Indeterminate: true, Disabled: true, Value: {{ indeterminateDisabled ? 'true' : 'false' }}
                    .mb-2
                      small.text--secondary.caption Use case: Read-only partial selections in forms or permission-locked states
                    yz-checkbox(v-model="indeterminateDisabled" :indeterminate="true" disabled label="Read-only Partial Selection")

                  // Indeterminate + Invalid example
                  template(v-else-if="checkbox.value === 'indeterminate-invalid'")
                    .mb-2
                      small.text--secondary Indeterminate: true, Invalid: true, Value: {{ indeterminateInvalid ? 'true' : 'false' }}
                    .mb-2
                      small.text--secondary.caption Use case: Validation errors in partial selection scenarios
                    yz-checkbox(v-model="indeterminateInvalid" :indeterminate="true" invalid label="Invalid Partial Selection")

                  // Indeterminate + Disabled + Invalid example
                  template(v-else-if="checkbox.value === 'indeterminate-combined'")
                    .mb-2
                      small.text--secondary Indeterminate: true, Disabled: true, Invalid: true, Value: {{ indeterminateCombined ? 'true' : 'false' }}
                    .mb-2
                      small.text--secondary.caption Use case: Complex form scenarios with multiple state constraints
                    yz-checkbox(v-model="indeterminateCombined" :indeterminate="true" disabled invalid label="Read-only Invalid State")

              // Regular grouped checkboxes
              template(v-else)
                .mb-3
                  strong Selected: {{ JSON.stringify($data[variation.checkboxes[0].model]) }}
                yz-checkbox-group(v-model="$data[variation.checkboxes[0].model]")
                  yz-checkbox(v-for="checkbox in variation.checkboxes" :key="checkbox.value" :value="checkbox.value" :label="checkbox.label" v-bind="checkbox.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                span.font-weight-medium Code Examples

              .code-examples
                // Special handling for optional validation code examples
                template(v-if="variation.id === 'optional-validation'")
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalFeaturesCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalFeaturesCode ? 'success' : ''" @click="copyCode(optionalFeaturesCode)")
                      span.font-weight-medium.ml-2 Optional feature preferences
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalFeaturesCode }}
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalConsentCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalConsentCode ? 'success' : ''" @click="copyCode(optionalConsentCode)")
                      span.font-weight-medium.ml-2 Optional consent preferences
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalConsentCode }}
                  .mb-3
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === optionalAddonsCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalAddonsCode ? 'success' : ''" @click="copyCode(optionalAddonsCode)")
                      span.font-weight-medium.ml-2 Optional addon services
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ optionalAddonsCode }}
                // Regular code examples for other variations
                template(v-else)
                  div(v-for="checkbox in variation.checkboxes" :key="checkbox.value" class="mb-3")
                    .d-flex.align-center.mb-1
                      yz-btn(icon-only size="md" :icon="copiedCode === checkbox.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === checkbox.code ? 'success' : ''" @click="copyCode(checkbox.code)")
                      span.font-weight-medium.ml-2 {{ checkbox.label }}
                    pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
                      code {{ checkbox.code }}

          // Individual checkbox
          template(v-else)
            .variation-preview.mb-4.pa-4.border.rounded
              yz-checkbox(v-model="variation.model" :value="variation.value" :label="variation.label" v-bind="variation.props")

            .variation-code
              .code-header.d-flex.align-center.mb-2
                yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
                span.font-weight-medium.ml-2 Code

              pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
                code {{ variation.code }}


    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // YzCheckboxGroup Props
      h4.mb-3 YzCheckboxGroup Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in checkboxGroupProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // YzCheckbox Props  
      h4.mb-3 YzCheckbox Props
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in checkboxProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Slots Documentation
      h4.mb-3.mt-6 YzCheckbox Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in checkboxSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}

      // Methods Documentation
      h4.mb-3.mt-6 YzCheckboxGroup Methods
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Method
                th Returns
                th Description
            tbody
              tr(v-for="method in checkboxGroupMethods" :key="method.name")
                td.font-weight-medium.text-no-wrap {{ method.name }}
                td.text--secondary.text-no-wrap {{ method.returns }}
                td.single-line {{ method.description }}
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null,
      basicCheckboxValue: ['option1'],
      disabledCheckboxValue: ['disabled-selected'],
      invalidCheckboxValue: ['invalid-selected'],
      gapCheckboxValue: ['gap-8'],
      rulesCheckboxValue: [],
      rulesCheckboxValue2: [],
      rulesCheckboxValue3: [],
      rulesValidationResult: null,
      customRulesValidationResult: null,
      rulesOnlyValidationResult: null,
      customValidationRules: [
        v => (v && v.length > 0) || 'At least one option required',
        v => v.includes('option1') || 'Option 1 must be selected'
      ],
      typesCheckboxValue: ['string-value'],
      indeterminateBasicValue: ['basic-indeterminate'],
      indeterminateDisabledValue: ['indeterminate-disabled'],
      indeterminateInvalidValue: ['indeterminate-invalid'],
      indeterminateCombinedValue: ['indeterminate-combined'],
      basicIndeterminate: false,
      // Combined state examples
      indeterminateDisabled: false,
      indeterminateInvalid: false,
      indeterminateCombined: false,
      slotCheckboxValue: ['text-label'],
      layoutHorizontalDefaultValue: ['layout-a'],
      layoutHorizontal32Value: ['layout-a'],
      layoutVerticalDefaultValue: ['layout-a'],
      layoutVertical16Value: ['layout-a'],
      
      // Optional Validation Examples Data
      optionalFeaturesValue: [],
      optionalConsentValue: [],
      optionalAddonsValue: [],
      optionalFeaturesValidationResult: null,
      optionalConsentValidationResult: null,
      optionalAddonsValidationResult: null,
      checkboxVariations: [
        {
          name: 'Basic Checkbox',
          id: 'basic-checkbox',
          description: 'Standard checkbox options',
          isGroup: true,
          checkboxes: [
            {
              label: 'Option 1',
              value: 'option1',
              model: 'basicCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="option1" label="Option 1")'
            },
            {
              label: 'Option 2',
              value: 'option2',
              model: 'basicCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="option2" label="Option 2")'
            },
            {
              label: 'Option 3',
              value: 'option3',
              model: 'basicCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="option3" label="Option 3")'
            }
          ]
        },
        {
          name: 'Disabled States',
          id: 'disabled-states',
          description: 'Disabled checkbox states',
          isGroup: true,
          checkboxes: [
            {
              label: 'Enabled',
              value: 'enabled',
              model: 'disabledCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="enabled" label="Enabled")'
            },
            {
              label: 'Disabled',
              value: 'disabled',
              model: 'disabledCheckboxValue',
              props: { disabled: true },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="disabled" label="Disabled" disabled)'
            },
            {
              label: 'Disabled & Selected',
              value: 'disabled-selected',
              model: 'disabledCheckboxValue',
              props: { disabled: true },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="disabled-selected" label="Disabled & Selected" disabled)'
            }
          ]
        },
        {
          name: 'Invalid States',
          id: 'invalid-states',
          description: 'Invalid checkbox states',
          isGroup: true,
          checkboxes: [
            {
              label: 'Valid',
              value: 'valid',
              model: 'invalidCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="valid" label="Valid")'
            },
            {
              label: 'Invalid',
              value: 'invalid',
              model: 'invalidCheckboxValue',
              props: { invalid: true },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="invalid" label="Invalid" invalid)'
            },
            {
              label: 'Invalid & Selected',
              value: 'invalid-selected',
              model: 'invalidCheckboxValue',
              props: { invalid: true },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="invalid-selected" label="Invalid & Selected" invalid)'
            }
          ]
        },
        {
          name: 'Validation with Rules',
          id: 'validation-rules',
          description: 'Checkbox group validation using rules array and v-form integration',
          isGroup: true,
          checkboxes: [
            {
              label: 'Required validation',
              value: 'rules-required',
              model: 'rulesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" required required-error-message="Please select at least one option")\n  yz-checkbox(value="option1" label="Option 1")\n  yz-checkbox(value="option2" label="Option 2")'
            },
            {
              label: 'Combined required + custom validation rules',
              value: 'rules-custom',
              model: 'rulesCheckboxValue2',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" required required-error-message="Please select at least one option" :rules="[v => v.includes(\'option1\') || \'Option 1 must be selected\']")\n  yz-checkbox(value="option1" label="Option 1")\n  yz-checkbox(value="option2" label="Option 2")'
            },
            {
              label: 'Required validation using only rules',
              value: 'rules-only',
              model: 'rulesCheckboxValue3',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" :rules="[v => (v && v.length > 0) || \'At least one option is required\']")\n  yz-checkbox(value="option1" label="Option 1")\n  yz-checkbox(value="option2" label="Option 2")'
            }
          ]
        },
        {
          name: 'Optional Validation',
          id: 'optional-validation',
          description: 'Optional fields that validate only when selections are made - useful for feature preferences, consent checkboxes, and optional configurations',
          isGroup: true,
          checkboxes: []
        },
        {
          name: 'Label Gap Variations',
          id: 'label-gap-variations',
          description: 'Different label gap spacing options',
          isGroup: true,
          checkboxes: [
            {
              label: '12px Gap',
              value: 'gap-12',
              model: 'gapCheckboxValue',
              props: { labelGap: '12px' },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="gap-12" label="12px Gap" label-gap="12px")'
            },
            {
              label: '8px Gap (default)',
              value: 'gap-8-default',
              model: 'gapCheckboxValue',
              props: { labelGap: '8px' },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="gap-8-default" label="8px Gap (default)" label-gap="8px")'
            },
            {
              label: '16px Gap',
              value: 'gap-16',
              model: 'gapCheckboxValue',
              props: { labelGap: '16px' },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="gap-16" label="16px Gap" label-gap="16px")'
            },
            {
              label: '24px Gap',
              value: 'gap-24',
              model: 'gapCheckboxValue',
              props: { labelGap: '24px' },
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="gap-24" label="24px Gap" label-gap="24px")'
            }
          ]
        },
        {
          name: 'Indeterminate State',
          id: 'indeterminate-state',
          description: 'Checkboxes with indeterminate state for partial selections',
          isGroup: true,
          checkboxes: [
            {
              label: 'Basic Indeterminate',
              value: 'basic-indeterminate',
              model: 'indeterminateBasicValue',
              props: {},
              code: 'yz-checkbox(v-model="basicIndeterminate" :indeterminate="true" label="Basic Indeterminate")'
            },
            {
              label: 'Indeterminate + Disabled',
              value: 'indeterminate-disabled',
              model: 'indeterminateDisabledValue',
              props: {},
              code: 'yz-checkbox(v-model="indeterminateDisabled" :indeterminate="true" disabled label="Read-only Partial Selection")\n\n// Use case: Displaying a partial selection state that cannot be modified\n// Common in forms where certain selections are locked based on user permissions'
            },
            {
              label: 'Indeterminate + Invalid',
              value: 'indeterminate-invalid',
              model: 'indeterminateInvalidValue',
              props: {},
              code: 'yz-checkbox(v-model="indeterminateInvalid" :indeterminate="true" invalid label="Invalid Partial Selection")\n\n// Use case: Validation errors in partial selection scenarios\n// When partial selection violates business rules or validation constraints'
            },
            {
              label: 'Indeterminate + Disabled + Invalid',
              value: 'indeterminate-combined',
              model: 'indeterminateCombinedValue',
              props: {},
              code: 'yz-checkbox(v-model="indeterminateCombined" :indeterminate="true" disabled invalid label="Read-only Invalid State")\n\n// Use case: Complex form scenarios with multiple state constraints\n// Showing validation errors on locked partial selections'
            }
          ]
        },
        {
          name: 'Different Value Types',
          id: 'different-value-types',
          description: 'Checkboxes with boolean, number, and object values',
          isGroup: true,
          checkboxes: [
            {
              label: 'String Value',
              value: 'string-value',
              model: 'typesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="string-value" label="String Value")'
            },
            {
              label: 'Boolean True',
              value: true,
              model: 'typesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(:value="true" label="Boolean True")'
            },
            {
              label: 'Boolean False',
              value: false,
              model: 'typesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(:value="false" label="Boolean False")'
            },
            {
              label: 'Number 123',
              value: 123,
              model: 'typesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(:value="123" label="Number 123")'
            },
            {
              label: 'Object Value',
              value: 'object-value',
              model: 'typesCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(:value="{ id: 1, name: \'Object\' }" label="Object Value")'
            }
          ]
        },
        {
          name: 'Label Slot',
          id: 'label-slot',
          description: 'Using custom content in the label slot',
          isGroup: true,
          checkboxes: [
            {
              label: 'Text Label',
              value: 'text-label',
              model: 'slotCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="text-label" label="Text Label")'
            },
            {
              label: 'Slot Label',
              value: 'slot-label',
              model: 'slotCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="slot-label")\n    template(#label)\n      span(v-html="\'Custom slot with <strong class="text-danger-500">HTML content</strong>\'")',
              isSlot: true
            },
            {
              label: 'Rich Content',
              value: 'rich-content',
              model: 'slotCheckboxValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="rich-content")\n    template(#label)\n      .d-flex.align-center\n        v-icon.mr-2 mdi-star\n        span Premium Option',
              isSlot: true,
              isRich: true
            }
          ]
        },
        {
          name: 'Layout Variations',
          id: 'layout-variations',
          description: 'Different layout orientations and custom spacing',
          isGroup: true,
          checkboxes: [
            {
              label: 'Vertical Layout (default)',
              value: 'vertical-default',
              model: 'layoutVerticalDefaultValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues")\n  yz-checkbox(value="layout-a" label="Layout A")\n  yz-checkbox(value="layout-b" label="Layout B")\n  yz-checkbox(value="layout-c" label="Layout C")',
              layoutType: 'vertical-default'
            },
            {
              label: 'Vertical with 32px spacing',
              value: 'vertical-32',
              model: 'layoutVertical16Value',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" spacing="32px")\n  yz-checkbox(value="layout-a" label="Layout A")\n  yz-checkbox(value="layout-b" label="Layout B")\n  yz-checkbox(value="layout-c" label="Layout C")',
              layoutType: 'vertical-32'
            },
            {
              label: 'Horizontal Layout',
              value: 'horizontal-default',
              model: 'layoutHorizontalDefaultValue',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" direction="horizontal")\n  yz-checkbox(value="layout-a" label="Layout A")\n  yz-checkbox(value="layout-b" label="Layout B")\n  yz-checkbox(value="layout-c" label="Layout C")',
              layoutType: 'horizontal-default'
            },
            {
              label: 'Horizontal with 32px spacing',
              value: 'horizontal-32',
              model: 'layoutHorizontal32Value',
              props: {},
              code: 'yz-checkbox-group(v-model="selectedValues" direction="horizontal" spacing="32px")\n  yz-checkbox(value="layout-a" label="Layout A")\n  yz-checkbox(value="layout-b" label="Layout B")\n  yz-checkbox(value="layout-c" label="Layout C")',
              layoutType: 'horizontal-32'
            }
          ]
        }
      ],
      checkboxGroupProps: [
        {
          name: 'v-model',
          type: 'Array',
          default: '[]',
          description: 'The selected values for the checkbox group'
        },
        {
          name: 'direction',
          type: 'String',
          default: 'vertical',
          description: 'Layout direction: "vertical" or "horizontal"'
        },
        {
          name: 'spacing',
          type: 'String|Number',
          default: '20',
          description: 'Spacing between checkboxes (in px)'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the checkbox group requires at least one selection'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message to display when required validation fails'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation rules. Each rule is a function that receives the value and returns true if valid, or an error message if invalid'
        }
      ],
      checkboxProps: [
        {
          name: 'value',
          type: 'Any',
          default: '-',
          description: 'The value of this checkbox option (required)'
        },
        {
          name: 'label',
          type: 'String',
          default: '',
          description: 'Checkbox label text'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the checkbox is disabled'
        },
        {
          name: 'name',
          type: 'String',
          default: '',
          description: 'Name attribute for the checkbox input'
        },
        {
          name: 'invalid',
          type: 'Boolean',
          default: 'false',
          description: 'Whether the checkbox is in invalid state'
        },
        {
          name: 'borderRadius',
          type: 'String',
          default: '5px',
          description: 'Border radius for the checkbox box'
        },
        {
          name: 'labelGap',
          type: 'String',
          default: '8px',
          description: 'Gap between checkbox and label'
        },
        {
          name: 'labelClass',
          type: 'String',
          default: 'yz-regular-body-sm',
          description: 'CSS class applied to the label text'
        },
        {
          name: 'indeterminate',
          type: 'Boolean',
          default: 'false',
          description: 'Controls whether the checkbox displays in the indeterminate state (shows a dash/minus symbol instead of checkmark)'
        }
      ],
      checkboxSlots: [
        {
          name: 'label',
          description: 'Custom content for the checkbox label'
        }
      ],
      checkboxGroupMethods: [
        {
          name: 'validate(force)',
          returns: 'Boolean',
          description: 'Manually triggers validation and returns validation result (true = valid, false = invalid). Pass force=true to show validation errors immediately'
        },
        {
          name: 'resetValidation()',
          returns: 'void',
          description: 'Clears validation errors without resetting the value'
        }
      ]
    }
  },
  computed: {
    // Optional Validation Rules
    optionalFeaturesRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['notifications', 'analytics', 'beta', 'marketing'].includes(item))) || 'Please select valid feature options'
      ]
    },
    optionalConsentRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['terms', 'privacy', 'cookies', 'newsletter'].includes(item))) || 'Please select valid consent options'
      ]
    },
    optionalAddonsRules() {
      return [
        v => !v || v.length === 0 || (v.every(item => ['backup', 'support', 'training', 'integration'].includes(item))) || 'Please select valid addon options'
      ]
    },
    
    // Code examples for optional validation
    optionalFeaturesCode() {
      return `yz-checkbox-group(v-model="featurePreferences" :rules="optionalFeaturesRules")
  yz-checkbox(value="notifications" label="Email notifications")
  yz-checkbox(value="analytics" label="Usage analytics")
  yz-checkbox(value="beta" label="Beta features access")
  yz-checkbox(value="marketing" label="Marketing communications")

// Validation rule
optionalFeaturesRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['notifications', 'analytics', 'beta', 'marketing'].includes(item))) || 'Please select valid feature options'
  ]
}`
    },
    optionalConsentCode() {
      return `yz-checkbox-group(v-model="consentPreferences" :rules="optionalConsentRules")
  yz-checkbox(value="terms" label="I agree to the terms of service")
  yz-checkbox(value="privacy" label="I accept the privacy policy")
  yz-checkbox(value="cookies" label="I allow cookies for analytics")
  yz-checkbox(value="newsletter" label="Subscribe to newsletter")

// Validation rule
optionalConsentRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['terms', 'privacy', 'cookies', 'newsletter'].includes(item))) || 'Please select valid consent options'
  ]
}`
    },
    optionalAddonsCode() {
      return `yz-checkbox-group(v-model="addonServices" :rules="optionalAddonsRules")
  yz-checkbox(value="backup" label="Cloud backup service")
  yz-checkbox(value="support" label="Priority customer support")
  yz-checkbox(value="training" label="Training and onboarding")
  yz-checkbox(value="integration" label="Third-party integrations")

// Validation rule
optionalAddonsRules() {
  return [
    v => !v || v.length === 0 || (v.every(item => ['backup', 'support', 'training', 'integration'].includes(item))) || 'Please select valid addon options'
  ]
}`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      // Update the route with the section hash
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    validateRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.rulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesValidationResult = null;
          }, 3000);
        }
      });
    },
    validateCustomRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.customRulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.customRulesValidationResult = null;
          }, 3000);
        }
      });
    },
    validateRulesOnlyCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesOnlyCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.validate === 'function') {
          const isValid = checkboxGroupRef.validate(true);
          this.rulesOnlyValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.rulesOnlyValidationResult = null;
          }, 1500);
        }
      });
    },
    resetRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.rulesValidationResult = null;
          this.rulesCheckboxValue = [];
        }
      });
    },
    resetCustomRulesCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.customRulesCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.customRulesValidationResult = null;
          this.rulesCheckboxValue2 = [];
        }
      });
    },
    resetRulesOnlyCheckboxGroup() {
      this.$nextTick(() => {
        const checkboxGroupRefArray = this.$refs.rulesOnlyCheckboxGroup;
        const checkboxGroupRef = Array.isArray(checkboxGroupRefArray) ? checkboxGroupRefArray[0] : checkboxGroupRefArray;
        if (checkboxGroupRef && typeof checkboxGroupRef.resetValidation === 'function') {
          checkboxGroupRef.resetValidation();
          this.rulesOnlyValidationResult = null;
          this.rulesCheckboxValue3 = [];
        }
      });
    },
    
    // Optional Validation Methods
    validateOptionalFeaturesForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalFeaturesForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalFeaturesValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalFeaturesValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalFeaturesForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalFeaturesForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalFeaturesValidationResult = null;
          this.optionalFeaturesValue = [];
        }
      });
    },
    validateOptionalConsentForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalConsentForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalConsentValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalConsentValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalConsentForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalConsentForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalConsentValidationResult = null;
          this.optionalConsentValue = [];
        }
      });
    },
    validateOptionalAddonsForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalAddonsForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalAddonsValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalAddonsValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalAddonsForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalAddonsForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalAddonsValidationResult = null;
          this.optionalAddonsValue = [];
        }
      });
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-3 > *:not(:last-child) {
  margin-bottom: 0.75rem;
}
</style>